// Flexible inventory import. Upload a free-form file, map its columns to product
// fields, then resolve/fix products, lengths and locations in an Excel-like review grid before
// import. The platform catalog and locations are loaded read-only.

import {
  useColumnMappings,
  useProposeMapping,
} from '~/pages/inventory-import/api/useColumnMappings';
import {
  Fragment,
  useEffect,
  useState,
  useMemo,
  useSyncExternalStore,
  type ComponentProps,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  ClipboardCheck,
  Columns3,
  Upload,
  type LucideIcon,
} from 'lucide-react';
import { CardWrapper } from '~/components/cards/CardWrapper';
import { BackButton, Button, ClipSpinner } from '~/components/ui';
import { UploadStep } from '~/pages/inventory-import/steps/UploadStep';
import { MappingStep } from '~/pages/inventory-import/steps/MappingStep';
import { ReviewStep } from '~/pages/inventory-import/steps/ReviewStep';
import { usePlatformCatalog } from '~/pages/inventory-import/api/usePlatformCatalog';
import { useLocations } from '~/pages/inventory-import/api/useLocations';
import {
  isTerminal,
  useExtractedDocument,
  useUploadDocument,
  type UploadRejection,
} from '~/api/documentExtraction';
import { MAX_UPLOAD_SIZE_MB } from '~/utils/uploadError';
import { DocumentProgress } from '~/pages/document-extraction/DocumentProgress';
import { ImportCard } from '~/pages/document-extraction/ImportCard';
import {
  exceedsImportLimit,
  importHandoffOf,
  type ImportHandoff,
} from '~/pages/document-extraction/importHandoff';
import { confidentMappingOf } from '~/pages/inventory-import/lib/uploadRouter';
import {
  headerSignature,
  type LearnedStore,
} from '~/pages/inventory-import/lib/autoMap';
import {
  proposalFailureKeyOf,
  proposalMappingOf,
  proposalRequestOf,
} from '~/pages/inventory-import/lib/mappingProposal';
import {
  getCatalog,
  setCatalog,
  subscribeCatalog,
} from '~/pages/inventory-import/lib/catalog';
import {
  getLocations,
  setLocations,
  subscribeLocations,
} from '~/pages/inventory-import/lib/locationCatalog';
import type {
  ColumnMapping,
  ParsedFile,
} from '~/pages/inventory-import/lib/types';

type Step = 'upload' | 'map' | 'review';
type View = Step | 'summary';

const STEPS: { id: Step; labelKey: string; Icon: LucideIcon }[] = [
  { id: 'upload', labelKey: 'steps.upload', Icon: Upload },
  { id: 'map', labelKey: 'steps.map_columns', Icon: Columns3 },
  { id: 'review', labelKey: 'steps.review_fix', Icon: ClipboardCheck },
];

function StepIndicator({
  step,
  showMap,
  onNavigate,
  canNavigate,
  t,
}: {
  step: Step;
  showMap: boolean;
  onNavigate: (s: Step) => void;
  canNavigate: (s: Step) => boolean;
  t: TFunction;
}) {
  const STEPS_VISIBLE = showMap ? STEPS : STEPS.filter((s) => s.id !== 'map');
  const current = STEPS_VISIBLE.findIndex((s) => s.id === step);
  return (
    <div
      className="flex items-center justify-between"
      aria-label={t('steps.aria_label')}
    >
      {STEPS_VISIBLE.map(({ id, labelKey, Icon }, i) => {
        const reached = i <= current;
        const clickable = id !== step && canNavigate(id);
        return (
          <Fragment key={id}>
            <button
              type="button"
              disabled={!clickable}
              aria-current={i === current ? 'step' : undefined}
              onClick={() => clickable && onNavigate(id)}
              className={`flex flex-1 flex-col items-center border-none bg-transparent p-0 ${
                clickable ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
              }`}
            >
              <div
                className={`mb-2 flex h-10 w-10 items-center justify-center rounded-full ${
                  reached ? 'bg-prim text-white' : 'bg-gray-100 text-gray-400'
                }`}
              >
                <Icon size={20} />
              </div>
              <p
                className={`text-center text-xs font-medium ${
                  reached ? 'text-prim' : 'text-gray-400'
                }`}
              >
                {t(labelKey)}
              </p>
            </button>
            {i < STEPS_VISIBLE.length - 1 && (
              <div
                className={`mx-2 h-0.5 flex-1 ${
                  i < current ? 'bg-prim' : 'bg-gray-200'
                }`}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

type ReviewBack = { label: string; onBack: () => void };

function initialStateOf(
  handoff: Partial<ImportHandoff> | null,
  documentFromUrl: string | undefined
) {
  const handedOver = Boolean(handoff?.parsedFile && handoff.mapping);
  const fromUrl: View = documentFromUrl ? 'summary' : 'upload';
  return {
    view: handedOver ? ('review' as View) : fromUrl,
    file: handoff?.parsedFile ?? null,
    mapping: handoff?.mapping ?? null,
    returnTo: handoff?.returnTo,
  };
}

// An extracted document always lands on its summary, however the user arrived: the coverage
// numbers are the thing to read before the grid, and a NEEDS_REVIEW document needs them most.
function activeViewOf(step: View, hasExtractedRows: boolean): View {
  return hasExtractedRows && step === 'upload' ? 'summary' : step;
}

function canNavigateTo(
  target: Step,
  reachable: { file: unknown; reviewFile: unknown; reviewMapping: unknown }
): boolean {
  if (target === 'map') return reachable.file != null;
  if (target === 'review') {
    return reachable.reviewFile != null && reachable.reviewMapping != null;
  }
  return true;
}

/** A file whose columns all name themselves skips the map; one never seen before asks the model. */
function routeOf(parsed: ParsedFile, learned: LearnedStore) {
  const confident = confidentMappingOf(parsed, learned);
  return {
    confident,
    askModel: !confident && !learned[headerSignature(parsed.headers)],
  };
}

/** Back from review goes where the rows came from: the caller's page, the summary, or the map. */
function reviewBackOf({
  returnTo,
  extractionDocId,
  t,
  navigate,
  showStep,
}: {
  returnTo: string | undefined;
  extractionDocId: string | undefined;
  t: TFunction;
  navigate: (to: string) => void;
  showStep: (next: View) => void;
}): ReviewBack {
  if (returnTo != null) {
    return {
      label: t('review.back_to_document'),
      onBack: () => navigate(returnTo),
    };
  }
  if (extractionDocId) {
    return {
      label: t('review.back_to_document'),
      onBack: () => showStep('summary'),
    };
  }
  return {
    label: t('review.back_to_mapping'),
    onBack: () => showStep('map'),
  };
}

// Read-only: load the platform catalog + org locations so products and locations resolve, and the
// organization's remembered column mappings, which seed the mapping step's auto-map.
function useImportCatalogs() {
  const {
    catalog,
    isLoading: catalogLoading,
    error: catalogError,
  } = usePlatformCatalog();
  const {
    locations,
    isLoading: locationsLoading,
    error: locationsError,
  } = useLocations();
  const { learned, isLoading: mappingsLoading } = useColumnMappings();
  const matchedCatalog = useSyncExternalStore(subscribeCatalog, getCatalog);
  const matchedLocations = useSyncExternalStore(
    subscribeLocations,
    getLocations
  );
  useEffect(() => {
    setCatalog(catalog);
  }, [catalog]);
  useEffect(() => {
    setLocations(locations);
  }, [locations]);

  const fetching = catalogLoading || locationsLoading || mappingsLoading;
  const [matchersReady, setMatchersReady] = useState(false);
  if (
    !matchersReady &&
    !fetching &&
    matchedCatalog === catalog &&
    matchedLocations === locations
  ) {
    setMatchersReady(true);
  }

  return {
    learned,
    catalogFailed: Boolean(catalogError),
    locationsFailed: Boolean(locationsError),
    catalogEmpty: catalog.length === 0,
    loading: fetching || !matchersReady,
  };
}

function useExtractionUpload(
  documentFromUrl: string | undefined,
  onUploaded: (documentId: string) => void
) {
  const upload = useUploadDocument();
  const [rejection, setRejection] = useState<UploadRejection | null>(null);
  const [documentId, setDocumentId] = useState<string | undefined>(
    documentFromUrl
  );
  const {
    document,
    isLoading: documentLoading,
    isLoadingItems: itemsLoading,
    isError: documentFailed,
    isMissing: documentMissing,
  } = useExtractedDocument(documentId);
  const handoff = useMemo(
    () => importHandoffOf(document, '/my-products/import'),
    [document]
  );
  const importable =
    handoff !== null && !exceedsImportLimit(handoff) ? handoff : null;

  const extract = (raw: File) => {
    setRejection(null);
    upload.mutate(raw, {
      onSuccess: (result) => {
        setDocumentId(result.id);
        onUploaded(result.id);
      },
      onError: (error) => setRejection(error),
    });
  };

  return {
    documentId,
    setDocumentId,
    document,
    documentLoading,
    itemsLoading,
    documentFailed,
    documentMissing,
    handoff,
    importable,
    rejection,
    uploading: upload.isPending,
    extract,
  };
}

function LoadNotices({
  catalogFailed,
  locationsFailed,
  catalogEmpty,
  t,
}: {
  catalogFailed: boolean;
  locationsFailed: boolean;
  catalogEmpty: boolean;
  t: TFunction;
}) {
  return (
    <>
      {catalogFailed && (
        <p className="mb-3 rounded-md bg-error-50 px-3 py-2 text-xs text-error-600">
          {t('header.catalog_error')}
        </p>
      )}
      {locationsFailed && (
        <p className="mb-3 rounded-md bg-error-50 px-3 py-2 text-xs text-error-600">
          {t('header.locations_error')}
        </p>
      )}
      {!catalogFailed && catalogEmpty && (
        <p className="mb-3 rounded-md bg-warning-100 px-3 py-2 text-xs text-warning-600">
          {t('header.catalog_empty')}
        </p>
      )}
    </>
  );
}

function ExtractionSummary({
  document,
  documentId,
  handoff,
  itemsLoading,
  failed,
  onContinue,
}: {
  document: ComponentProps<typeof DocumentProgress>['document'];
  documentId: string;
  handoff: ImportHandoff | null;
  itemsLoading: boolean;
  failed: boolean;
  onContinue: () => void;
}) {
  const { t } = useTranslation('document-extraction');
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-3">
      <DocumentProgress document={document} />
      {itemsLoading && (
        <div data-testid="items-loading">
          <ClipSpinner color="currentColor" />
        </div>
      )}
      {failed && (
        <p data-testid="load-error" className="text-sm text-error-700">
          {t('items.load_failed')}
        </p>
      )}
      {isTerminal(document.status) && !itemsLoading && !failed && (
        <ImportCard
          items={document.items}
          importHandoff={handoff}
          onContinue={onContinue}
        />
      )}
      <Button
        variant="ghost-primary"
        size="sm"
        width="auto"
        onClick={() => navigate(`/document-extraction?doc=${documentId}`)}
      >
        {t('import.view_details')}
      </Button>
    </div>
  );
}

function SummaryPanel({
  extraction,
  documentId,
  onContinue,
}: {
  extraction: ReturnType<typeof useExtractionUpload>;
  documentId: string;
  onContinue: () => void;
}) {
  const { document } = extraction;
  if (!document) {
    return <DocumentNotice extraction={extraction} />;
  }
  return (
    <ExtractionSummary
      document={document}
      documentId={documentId}
      handoff={extraction.handoff}
      itemsLoading={extraction.itemsLoading}
      failed={extraction.documentFailed}
      onContinue={onContinue}
    />
  );
}

function DocumentNotice({
  extraction,
}: {
  extraction: ReturnType<typeof useExtractionUpload>;
}) {
  const { t } = useTranslation('document-extraction');
  if (extraction.documentLoading) {
    return (
      <p data-testid="document-loading" className="text-sm text-neut-sec">
        {t('import.document_loading')}
      </p>
    );
  }
  const key = extraction.documentMissing
    ? 'import.document_missing'
    : 'import.document_load_failed';
  return (
    <p data-testid="document-notice" className="text-sm text-error-700">
      {t(key)}
    </p>
  );
}

function UploadPanel({
  rejection,
  uploading,
  onExtract,
  onParsed,
}: {
  rejection: UploadRejection | null;
  uploading: boolean;
  onExtract: (raw: File) => void;
  onParsed: (parsed: ParsedFile) => void;
}) {
  const { t } = useTranslation('document-extraction');
  return (
    <>
      {uploading && (
        <p className="mb-3 rounded-md bg-neut-50 px-3 py-2 text-xs text-neut-sec">
          {t('upload.hint')}
        </p>
      )}
      <UploadStep
        onExtract={onExtract}
        onParsed={onParsed}
        disabled={uploading}
        externalError={
          rejection
            ? t(rejection.messageKey, { maxSizeMB: MAX_UPLOAD_SIZE_MB })
            : null
        }
      />
    </>
  );
}

function MapPanel({
  proposing,
  failureKey,
  t,
  ...step
}: ComponentProps<typeof MappingStep> & {
  proposing: boolean;
  failureKey: string | undefined;
  t: TFunction;
}) {
  if (proposing) {
    return <p className="text-sm text-neut-sec">{t('mapping.proposing')}</p>;
  }
  return (
    <>
      {failureKey && (
        <p
          data-testid="proposal-failed"
          className="mb-3 rounded-md bg-warning-100 px-3 py-2 text-xs text-warning-600"
        >
          {t(failureKey)}
        </p>
      )}
      <MappingStep {...step} />
    </>
  );
}

type ImportBodyProps = {
  view: View;
  t: TFunction;
  catalogs: ReturnType<typeof useImportCatalogs>;
  extraction: ReturnType<typeof useExtractionUpload>;
  file: ParsedFile | null;
  reviewFile: ParsedFile | null;
  reviewMapping: ColumnMapping | null;
  proposing: boolean;
  proposalFailureKey: string | undefined;
  proposed: ComponentProps<typeof MappingStep>['proposed'];
  back: ReviewBack;
  onParsed: (parsed: ParsedFile) => void;
  onMapped: (mapping: ColumnMapping) => void;
  showStep: (next: View) => void;
};

function ImportBody({
  view,
  t,
  catalogs,
  extraction,
  file,
  reviewFile,
  reviewMapping,
  proposing,
  proposalFailureKey,
  proposed,
  back,
  onParsed,
  onMapped,
  showStep,
}: ImportBodyProps) {
  if (catalogs.loading) {
    return <p className="text-sm text-neut-sec">{t('header.loading')}</p>;
  }
  const { documentId } = extraction;
  const onSummary = view === 'upload' || view === 'summary';

  return (
    <>
      <LoadNotices
        catalogFailed={catalogs.catalogFailed}
        locationsFailed={catalogs.locationsFailed}
        catalogEmpty={catalogs.catalogEmpty}
        t={t}
      />

      {onSummary && documentId && (
        <SummaryPanel
          extraction={extraction}
          documentId={documentId}
          onContinue={() => showStep('review')}
        />
      )}

      {view === 'upload' && !documentId && (
        <UploadPanel
          rejection={extraction.rejection}
          uploading={extraction.uploading}
          onExtract={extraction.extract}
          onParsed={onParsed}
        />
      )}

      {view === 'map' && file && (
        <MapPanel
          proposing={proposing}
          failureKey={proposalFailureKey}
          t={t}
          file={file}
          learned={catalogs.learned}
          proposed={proposed}
          onBack={() => showStep('upload')}
          onConfirm={onMapped}
        />
      )}

      {view === 'review' && reviewFile && reviewMapping && (
        <ReviewStep
          file={reviewFile}
          mapping={reviewMapping}
          backLabel={back.label}
          onBack={back.onBack}
        />
      )}
    </>
  );
}

export default function InventoryImport() {
  const { t } = useTranslation('inventory-import');
  // Trusted unchecked: only DocumentExtraction's handoff sets it; every read is optional.
  const handoff = useLocation().state as Partial<ImportHandoff> | null;
  const [searchParams, setSearchParams] = useSearchParams();
  const documentFromUrl = searchParams.get('doc') ?? undefined;
  const [initial] = useState(() => initialStateOf(handoff, documentFromUrl));
  // Held here, not read from router state: Start over must be able to clear it, and the state
  // stays attached to this history entry.
  const [returnTo, setReturnTo] = useState(initial.returnTo);
  const [flow, setFlow] = useState<{ view: View; mapReached: boolean }>({
    view: initial.view,
    mapReached: false,
  });
  const showStep = (next: View) =>
    setFlow((current) => ({
      view: next,
      mapReached: current.mapReached || next === 'map',
    }));
  const [file, setFile] = useState<ParsedFile | null>(initial.file);
  const [mapping, setMapping] = useState<ColumnMapping | null>(initial.mapping);
  const navigate = useNavigate();
  const catalogs = useImportCatalogs();
  const proposeMapping = useProposeMapping();
  const extraction = useExtractionUpload(documentFromUrl, (documentId) => {
    setFile(null);
    setMapping(null);
    proposeMapping.reset();
    setFlow({ view: 'upload', mapReached: false });
    setSearchParams({ doc: documentId }, { replace: true });
  });
  const proposed = useMemo(
    () => proposalMappingOf(proposeMapping.data?.proposeSellerImportMapping),
    [proposeMapping.data]
  );

  const reviewFile = extraction.importable?.parsedFile ?? file;
  const reviewMapping = extraction.importable?.mapping ?? mapping;
  const activeStep = activeViewOf(flow.view, extraction.importable != null);

  const handleParsed = (parsed: ParsedFile) => {
    const route = routeOf(parsed, catalogs.learned);
    setFile(parsed);
    if (route.askModel) proposeMapping.mutate(proposalRequestOf(parsed));
    else proposeMapping.reset();
    if (route.confident) setMapping(route.confident);
    showStep(route.confident ? 'review' : 'map');
  };

  const reset = () => {
    setFile(null);
    setMapping(null);
    extraction.setDocumentId(undefined);
    setReturnTo(undefined);
    setSearchParams({}, { replace: true });
    setFlow({ view: 'upload', mapReached: false });
    proposeMapping.reset();
  };

  return (
    <div className="flex flex-col gap-6">
      <BackButton link="/my-products" label={t('header.back')} />

      <header className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h1 className="mb-1 text-2xl font-semibold text-neut-prim">
            {t('header.title')}
          </h1>
          <p className="text-sm text-neut-sec">{t('header.subtitle')}</p>
        </div>
        {(file || extraction.documentId) && (
          <button
            type="button"
            onClick={reset}
            className="shrink-0 text-xs text-neut-sec underline hover:text-neut-prim"
          >
            {t('header.start_over')}
          </button>
        )}
      </header>

      <CardWrapper>
        <StepIndicator
          step={activeStep === 'summary' ? 'upload' : activeStep}
          showMap={activeStep === 'map' || flow.mapReached}
          onNavigate={showStep}
          canNavigate={(target) =>
            canNavigateTo(target, { file, reviewFile, reviewMapping })
          }
          t={t}
        />
      </CardWrapper>

      <CardWrapper>
        <ImportBody
          view={activeStep}
          t={t}
          catalogs={catalogs}
          extraction={extraction}
          file={file}
          reviewFile={reviewFile}
          reviewMapping={reviewMapping}
          proposing={proposeMapping.isPending}
          proposalFailureKey={proposalFailureKeyOf(proposeMapping.error)}
          proposed={proposed}
          back={reviewBackOf({
            returnTo,
            extractionDocId: extraction.documentId,
            t,
            navigate,
            showStep,
          })}
          onParsed={handleParsed}
          onMapped={(confirmed) => {
            setMapping(confirmed);
            showStep('review');
          }}
          showStep={showStep}
        />
      </CardWrapper>
    </div>
  );
}
